import { useMemo } from "react"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import useHomeDashboard from "./hooks/useHomeDashboard"
import {
  MetricsCardsGrid,
  MarketingTrendsSection,
  MerchandisingTrendsSection,
  AdCreativeReportingSection,
} from "./components/molecules"
import WaterfallChart, {
  type WaterfallDataPoint,
} from "~/ui-rtk/components/ui/charts/WaterfallChart/WaterfallChart"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import { Button, DateRangePicker } from "~/ui-rtk/components/ui/controls"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import { useLastUpdateDate } from "~/ui-rtk/hooks/cube"
import { AggregationType } from "~/ui-rtk/constants/charts"
import useAggregationGroups from "~/ui-rtk/hooks/aggregation-groups"

const dayjs = useDate()

type HomeDashboardV2Props = {
  className?: string
}

export default function HomeDashboardV2({ className }: HomeDashboardV2Props) {
  // Get last update date for max date restriction
  const { lastDate, isLoading: isLastDateLoading } = useLastUpdateDate()

  // Cap max date at yesterday so the default view only shows complete data
  const lastCompleteDate = useMemo(() => {
    const yesterday = dayjs().subtract(1, "day").startOf("day").toDate()
    if (!lastDate) return yesterday
    const lastDateObj = new Date(lastDate)
    return lastDateObj > yesterday ? yesterday : lastDateObj
  }, [lastDate])

  // Use the same date range system as BrandValueOverviewDashboard
  const { dateRange, compareRange } = useMinMaxDateRange(
    undefined,
    lastCompleteDate,
    undefined,
    false,
    undefined,
    "last7Days", // Default preset: last 7 days
  )
  const { granularity, setPeriod, setComparePeriod, setGranularity } =
    usePeriod()

  const aggregationGroups = useAggregationGroups()

  // Convert dateRange from [Date, Date] to {start: Date, end: Date} for useHomeDashboard
  const dateRangeObj = useMemo(
    () => ({
      start: dateRange[0],
      end: dateRange[1],
    }),
    [dateRange],
  )

  const compareRangeObj = useMemo(
    () =>
      compareRange
        ? {
            start: compareRange[0],
            end: compareRange[1],
          }
        : null,
    [compareRange],
  )

  const compareEnabled = !!compareRange

  const {
    revenueType,
    isShopifyConnected,
    setRevenueType,
    metricCards,
    isLoading,
    // Chart data
    marketingChartData,
    compareMarketingChartData,
    isMarketingChartLoading,
    // Table data
    topCollections,
    isCollectionsLoading,
    topProducts,
    isProductsLoading,
    hasMerchandisingData,
    // Waterfall data
    waterfallData,
    isAmazonConnected,
  } = useHomeDashboard(dateRangeObj, compareRangeObj, granularity)

  // Handle date range changes
  const handleChangePeriods = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    setPeriod(dayjs(start).format(DATE_FORMAT), dayjs(end).format(DATE_FORMAT))

    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  const handleSetDataAggType = (aggType: AggregationType) => {
    setGranularity(aggType)
  }

  // Build waterfall chart data from real metrics
  const waterfallChartData = useMemo<WaterfallDataPoint[]>(() => {
    const { totalRevenue, orderCosts, adSpend, contributionMargin } =
      waterfallData

    // Only show chart if we have revenue data
    if (totalRevenue === null || totalRevenue === 0) return []

    const data: WaterfallDataPoint[] = [
      { name: "Total Revenue", value: totalRevenue, isTotal: true },
    ]

    // Add order costs (COGS) if available
    if (orderCosts !== null && orderCosts !== 0) {
      data.push({ name: "Order Costs", value: -Math.abs(orderCosts) })
    }

    // Add ad spend if available
    if (adSpend !== null && adSpend !== 0) {
      data.push({ name: "Ad Spend", value: -Math.abs(adSpend) })
    }

    // Add net profit as final total
    if (contributionMargin !== null) {
      data.push({
        name: "Net Profit",
        value: contributionMargin,
        isTotal: true,
      })
    }

    return data
  }, [waterfallData])

  // Show loading state until max date is loaded (after all hooks are called)
  if (isLastDateLoading) {
    return (
      <div
        className={cn(
          "home-dashboard-v2 flex items-center justify-center min-h-screen w-full",
          className,
        )}
      >
        <div className="flex flex-col items-center gap-4">
          <Loader size={60} />
          <p className="text-system-gray-400">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("home-dashboard-v2 space-y-8", className)}>
      {/* Header Section */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">
            Dashboard
          </h1>
          <p className="text-system-gray-400">
            Your quick metrics digest for informed decisions
          </p>
        </div>
      </div>

      {/* Date Range Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Revenue Type Tabs - Only show when Shopify is connected */}
        {isShopifyConnected && (
          <div className="inline-flex items-center rounded-lg bg-system-blue-800 p-1 gap-1">
            <button
              onClick={() => setRevenueType("total")}
              className={cn(
                "px-6 py-2.5 text-sm font-medium rounded-md transition-all duration-200",
                revenueType === "total"
                  ? "bg-system-blue-600 text-white shadow-sm"
                  : "text-system-gray-300 hover:text-white hover:bg-system-blue-700",
              )}
            >
              Total
            </button>
            <button
              onClick={() => setRevenueType("ecomm")}
              className={cn(
                "px-6 py-2.5 text-sm font-medium rounded-md transition-all duration-200",
                revenueType === "ecomm"
                  ? "bg-system-blue-600 text-white shadow-sm"
                  : "text-system-gray-300 hover:text-white hover:bg-system-blue-700",
              )}
            >
              Ecomm
            </button>
            <button
              onClick={() => setRevenueType("retail")}
              className={cn(
                "px-6 py-2.5 text-sm font-medium rounded-md transition-all duration-200",
                revenueType === "retail"
                  ? "bg-system-blue-600 text-white shadow-sm"
                  : "text-system-gray-300 hover:text-white hover:bg-system-blue-700",
              )}
            >
              DTC-Retail
            </button>
          </div>
        )}

        {/* Granularity & Date Picker */}
        <div className="flex items-center gap-4">
          {/* Granularity Picker */}
          <div className="join">
            {aggregationGroups.length > 1
              ? aggregationGroups.map(({ type, long }) => (
                  <Button
                    variant={{ variant: "solid", color: "blue" }}
                    key={type}
                    title={long}
                    className={cn(
                      "join-item border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md",
                      { "bg-system-blue-800": type !== granularity },
                      { "bg-system-blue-600": type === granularity },
                    )}
                    onClick={() => handleSetDataAggType(type)}
                  >
                    {long}
                  </Button>
                ))
              : null}
          </div>

          {/* Date Picker */}
          {dateRange?.[0] &&
            dateRange?.[1] &&
            isValidDate(dateRange[0]) &&
            isValidDate(dateRange[1]) && (
              <DateRangePicker
                start={dateRange[0]}
                end={dateRange[1]}
                defaultPreset="last7Days"
                granularity={granularity}
                compareStart={
                  compareRange?.[0] && isValidDate(compareRange[0])
                    ? compareRange[0]
                    : undefined
                }
                lastUpdatedDate={lastDate ? new Date(lastDate) : undefined}
                comparisonEnabled={true}
                compareEnd={
                  compareRange?.[1] && isValidDate(compareRange[1])
                    ? compareRange[1]
                    : undefined
                }
                onChange={handleChangePeriods}
              />
            )}
        </div>
      </div>

      {/* Metrics Cards Grid - 6 Cards */}
      <MetricsCardsGrid cards={metricCards} isLoading={isLoading} />

      {/* Marketing & Merchandising Trends - Side by Side on Large Screens */}
      <div className="grid grid-cols-1 xl:grid-cols-9 gap-6">
        <div className="xl:col-span-5">
          <MarketingTrendsSection
            dateRange={dateRangeObj}
            chartData={marketingChartData}
            compareChartData={compareMarketingChartData}
            compareEnabled={compareEnabled}
            isLoading={isMarketingChartLoading}
            isShopifyConnected={isShopifyConnected}
            isAmazonConnected={isAmazonConnected}
          />
        </div>
        <div className="xl:col-span-4">
          <MerchandisingTrendsSection
            dateRange={dateRangeObj}
            topCollections={topCollections}
            topProducts={topProducts}
            isCollectionsLoading={isCollectionsLoading}
            isProductsLoading={isProductsLoading}
            hasMerchandisingData={hasMerchandisingData}
          />
        </div>
      </div>

      {/* Revenue Waterfall Section - Only show when Shopify is connected and we have data */}
      {isShopifyConnected && waterfallChartData.length > 0 && (
        <Card
          rounded
          className="p-system-lg bg-basic-dark-blue border border-basic-blue"
        >
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-white">
              Revenue Breakdown
            </h2>
            <p className="text-system-gray-400 text-sm">
              Visualize revenue flow from gross to net profit
            </p>
          </div>
          <WaterfallChart data={waterfallChartData} height={350} />
        </Card>
      )}

      {/* Ad Creative Reporting Section */}
      <AdCreativeReportingSection
        dateRange={dateRangeObj}
        isLoading={isLoading}
      />
    </div>
  )
}
